import type { FreightMatching } from "@x-mile/api-contract";
import { isEqual } from "date-fns";
import { useTranslation } from "react-i18next";
import { assertNever } from "../../shared/lib/assertNever";
import { excludeNull } from "../../shared/lib/collection";
import {
  fromIso8601Date,
  fromIso8601DateTime,
  fromIso8601Time,
  toLocaleTextAsDate,
  toLocaleTextAsTime,
} from "../../shared/lib/datetime";

export type Destination = FreightMatching.schemas.OfferCargo["destinations"][0];
export type Deadline = Partial<{
  deadlineDate:
    | FreightMatching.schemas.OfferCargo["loadingOn"]
    | FreightMatching.schemas.OfferCargo["unloadingOn"];
  deadlineType: Destination["deadlineType"];
  deadlineTime: Destination["deadlineTime"];
  deadlineTimeEnd: Destination["deadlineTimeEnd"];
}>;

export const calculateDeadlineDateFromOfferCargoDestination = (
  taskContent: "loading" | "transit" | "unloading",
  offerCargo: Pick<
    FreightMatching.schemas.OfferCargo,
    "loadingOn" | "unloadingOn"
  >,
): string | undefined => {
  return taskContent === "loading"
    ? offerCargo.loadingOn
    : taskContent === "transit"
      ? undefined
      : offerCargo.unloadingOn;
};

export const calculateDeadlineFromOfferCargoDestination = (
  taskContent: "loading" | "transit" | "unloading",
  offerCargo: Pick<
    FreightMatching.schemas.OfferCargo,
    "loadingOn" | "unloadingOn"
  >,
  offerCargoDestination: FreightMatching.schemas.OfferCargo["destinations"][0],
): Deadline => {
  return {
    deadlineDate: calculateDeadlineDateFromOfferCargoDestination(
      taskContent,
      offerCargo,
    ),
    deadlineType: offerCargoDestination.deadlineType,
    deadlineTime: offerCargoDestination.deadlineTime,
    deadlineTimeEnd: offerCargoDestination.deadlineTimeEnd,
  };
};

export const useformatDeadline = (
  deadline: Deadline | undefined | null,
): {
  deadlineText: string | null;
} => {
  const { t } = useTranslation();

  if (!deadline) {
    return {
      deadlineText: null,
    };
  }
  const { deadlineDate, deadlineType, deadlineTime, deadlineTimeEnd } =
    deadline;
  if (!deadlineType) {
    return {
      deadlineText: null,
    };
  }
  const deadlineDateText = deadlineDate
    ? `${toLocaleTextAsDate(fromIso8601Date(deadlineDate))} `
    : null;
  const deadlineTimeText = deadlineTime
    ? toLocaleTextAsTime(fromIso8601Time(deadlineTime))
    : null;
  if (deadlineType === "term" && deadlineTimeText && deadlineTimeEnd) {
    const deadlineTimeRangeText = `${deadlineTimeText} 〜 ${toLocaleTextAsTime(fromIso8601Time(deadlineTimeEnd))}`;
    return {
      deadlineText: deadlineDateText
        ? `${deadlineDateText} ${deadlineTimeRangeText}`
        : deadlineTimeRangeText,
    };
  }
  const translatedDeadlineType = t(`deadlineType.${deadlineType}`);
  const texts = excludeNull([
    deadlineDateText,
    deadlineTimeText,
    deadlineTimeText ? translatedDeadlineType : "指定なし",
  ]);
  return {
    deadlineText: texts.join(" "),
  };
};

export const areDeadlinesEqual = (
  criterionDeadline: Partial<Deadline>,
  proposedDeadline: Partial<Deadline>,
): boolean => {
  return (
    criterionDeadline.deadlineDate === proposedDeadline.deadlineDate &&
    criterionDeadline.deadlineType === proposedDeadline.deadlineType &&
    (criterionDeadline.deadlineTime === proposedDeadline.deadlineTime ||
      (!criterionDeadline.deadlineTime && !proposedDeadline.deadlineTime)) &&
    (criterionDeadline.deadlineTimeEnd === proposedDeadline.deadlineTimeEnd ||
      (!criterionDeadline.deadlineTimeEnd && !proposedDeadline.deadlineTimeEnd))
  );
};

export const checkProposedDeadlineSatisfied = (
  criterionDeadline: Partial<Deadline>,
  proposedDeadline: Partial<Deadline>,
): boolean => {
  const {
    deadlineDate: criterionDeadlineDate,
    deadlineType: criterionDeadlineType,
    deadlineTime: criterionDeadlineTime,
    deadlineTimeEnd: criterionDeadlineTimeEnd,
  } = criterionDeadline;
  const {
    deadlineDate: proposedDeadlineDate,
    deadlineType: proposedDeadlineType,
    deadlineTime: proposedDeadlineTime,
    deadlineTimeEnd: proposedDeadlineTimeEnd,
  } = proposedDeadline;

  if (!criterionDeadlineType || !criterionDeadlineTime) {
    return true;
  }
  if (
    !proposedDeadlineDate &&
    !proposedDeadlineType &&
    !proposedDeadlineTime &&
    !proposedDeadlineTimeEnd
  ) {
    // 何も提案されていない状態
    return true;
  }

  if (areDeadlinesEqual(criterionDeadline, proposedDeadline)) {
    return true;
  }

  const criterionDeadlineDateTime = criterionDeadlineDate
    ? fromIso8601DateTime(`${criterionDeadlineDate}T${criterionDeadlineTime}`)
    : fromIso8601Time(criterionDeadlineTime);
  const criterionDeadlineDateTimeEnd =
    criterionDeadlineType === "term" && criterionDeadlineTimeEnd
      ? criterionDeadlineDate
        ? fromIso8601DateTime(
            `${criterionDeadlineDate}T${criterionDeadlineTimeEnd}`,
          )
        : fromIso8601Time(criterionDeadlineTimeEnd)
      : undefined;
  if (!proposedDeadlineType || !proposedDeadlineTime) {
    return false;
  }

  console.log({
    criterionDeadline,
    proposedDeadline,
  });

  const proposedDeadlineDateTime = proposedDeadlineDate
    ? fromIso8601DateTime(`${proposedDeadlineDate}T${proposedDeadlineTime}`)
    : fromIso8601Time(proposedDeadlineTime);
  const proposedDeadlineDateTimeEnd =
    proposedDeadlineType === "term" && proposedDeadlineTimeEnd
      ? proposedDeadlineDate
        ? fromIso8601DateTime(
            `${proposedDeadlineDate}T${proposedDeadlineTimeEnd}`,
          )
        : fromIso8601Time(proposedDeadlineTimeEnd)
      : undefined;
  // 車主提案が曖昧になることを避けるため、全ての荷主提案に対してjust か term 以外提案できない
  // UI側で「ちょうど」と「時間指定」以外選択できないようにしているが、念の為validationする
  switch (criterionDeadlineType) {
    case "till": {
      switch (proposedDeadlineType) {
        case "just":
          return proposedDeadlineDateTime <= criterionDeadlineDateTime;
        case "term":
          if (!proposedDeadlineDateTimeEnd) {
            return false;
          }
          return proposedDeadlineDateTimeEnd <= criterionDeadlineDateTime;
        case "till":
        case "after":
        case "earliest":
          return false;
        default:
          assertNever(proposedDeadlineType);
          return false;
      }
    }
    case "just": {
      // justにはjustしか提案できない
      switch (proposedDeadlineType) {
        case "just":
          return isEqual(proposedDeadlineDateTime, criterionDeadlineDateTime);
        case "term":
          return false;
        case "till":
        case "after":
        case "earliest":
          return false;
        default:
          assertNever(proposedDeadlineType);
          return false;
      }
    }
    case "after": {
      switch (proposedDeadlineType) {
        case "just":
          return proposedDeadlineDateTime >= criterionDeadlineDateTime;
        case "term":
          return proposedDeadlineDateTime >= criterionDeadlineDateTime;
        case "till":
        case "after":
        case "earliest":
          return false;
        default:
          assertNever(proposedDeadlineType);
          return false;
      }
    }
    case "earliest": {
      switch (proposedDeadlineType) {
        case "just":
          return proposedDeadlineDateTime <= criterionDeadlineDateTime;
        case "term":
          if (!proposedDeadlineDateTimeEnd) {
            return false;
          }
          return proposedDeadlineDateTimeEnd <= criterionDeadlineDateTime;
        case "till":
        case "after":
        case "earliest":
          return false;
        default:
          assertNever(proposedDeadlineType);
          return false;
      }
    }
    case "term": {
      switch (proposedDeadlineType) {
        case "just":
          console.log({
            criterionDeadlineDateTime,
            proposedDeadlineDateTime,
          });
          if (!criterionDeadlineDateTimeEnd) {
            return true;
          }
          return (
            criterionDeadlineDateTime <= proposedDeadlineDateTime &&
            proposedDeadlineDateTime <= criterionDeadlineDateTimeEnd
          );
        case "term":
          if (!criterionDeadlineDateTimeEnd) {
            return true;
          }
          if (!proposedDeadlineDateTimeEnd) {
            return false;
          }
          return (
            criterionDeadlineDateTime <= proposedDeadlineDateTime &&
            proposedDeadlineDateTimeEnd <= criterionDeadlineDateTimeEnd
          );
        case "till":
        case "after":
        case "earliest":
          return false;
        default:
          assertNever(proposedDeadlineType);
          return false;
      }
    }
    default:
      assertNever(criterionDeadlineType);
      return false;
  }
};
